import { isSearchResultJiraBoard, isSearchResultJiraIssue } from '@atlassian/search-client/jira';
import type {
	QuickFindDetailIds,
	QuickFindDetails,
} from '@atlassian/search-client/jira-quick-find-details';
import {
	isPullRequestEntity,
	isSearchResultGraphEntity,
} from '@atlassian/search-client/third-party-graph-entity';

import type { SearchResults } from './types';

/** Return nothing when no result needs a follow-up request. */
export const collectJiraQuickFindDetailIds = ({
	result,
}: SearchResults): QuickFindDetailIds | undefined => {
	const issueIds = new Set<string>();
	const boardIds = new Set<string>();
	const pullRequestIds = new Set<string>();
	for (const { data } of result) {
		if (isSearchResultJiraIssue(data)) {
			issueIds.add(data.id);
		} else if (isSearchResultJiraBoard(data)) {
			boardIds.add(data.id);
		} else if (isSearchResultGraphEntity(data) && isPullRequestEntity(data)) {
			pullRequestIds.add(data.id);
		}
	}
	return issueIds.size || boardIds.size || pullRequestIds.size
		? { issueIds: [...issueIds], boardIds: [...boardIds], pullRequestIds: [...pullRequestIds] }
		: undefined;
};

export const markJiraAvatarsLoading = (source: SearchResults): SearchResults => ({
	...source,
	result: source.result.map((result) =>
		isSearchResultJiraIssue(result.data) ? { ...result, isAvatarLoading: true } : result,
	),
});

/** Merge into the original results, preserving raw URLs and tolerating partial data. */
export const mergeJiraQuickFindDetails = (
	source: SearchResults,
	details?: QuickFindDetails | null,
): SearchResults => {
	const avatars = new Map(
		(details?.jira_issuesByIds ?? []).flatMap((issue) => {
			const avatar = issue?.issueType?.avatar?.medium;
			return issue?.id && avatar ? [[issue.id, avatar] as const] : [];
		}),
	);
	const boards = new Map(
		(details?.jira_boardsByIds ?? []).flatMap((board) =>
			board?.id && board.iconUrl ? [[board.id, board.iconUrl] as const] : [],
		),
	);
	const pullRequests = new Map(
		(details?.external_entities?.pullRequest ?? []).flatMap((pr) =>
			pr?.id && pr.pullRequestStatus ? [[pr.id, pr.pullRequestStatus] as const] : [],
		),
	);
	return {
		...source,
		result: source.result.map((result) => {
			const { data } = result;
			const avatar = avatars.get(data.id);
			const boardIconUrl = boards.get(data.id);
			const pullRequestStatus = pullRequests.get(data.id);
			if (avatar && isSearchResultJiraIssue(data)) {
				return { ...result, data: { ...data, iconUrl: avatar } };
			}
			if (boardIconUrl && isSearchResultJiraBoard(data)) {
				return { ...result, data: { ...data, iconUrl: boardIconUrl } };
			}
			if (pullRequestStatus && isSearchResultGraphEntity(data) && isPullRequestEntity(data)) {
				return { ...result, data: { ...data, entity: { ...data.entity, pullRequestStatus } } };
			}
			return result;
		}),
	};
};
